/**
 * The interactive report's charts (12 Oct), drawn like the 9 Oct overview:
 * donuts, rings, the age bar, raised vs resolved per day, the daily stacked
 * bars by channel with the IT HelpDesk share, and the team bars. Every mark
 * is clickable (it opens the tickets behind it); the keys under each chart
 * are buttons too, so the keyboard reaches every number. Hovering a mark
 * names it and its count.
 */

import { cn } from "@norican/react";
import type { KeyboardEvent, ReactNode } from "react";

import { C, donutArcs, goalTick, isWeekend, pctOf } from "./insights-helpers";

export interface Part {
  id: string;
  label: string;
  value: number;
  color: string;
  extra?: string;
  onSelect?: () => void;
}

function activate(event: KeyboardEvent, run?: () => void) {
  if (run && (event.key === "Enter" || event.key === " ")) {
    event.preventDefault();
    run();
  }
}

/** A donut of parts, the total (or `big`) in the middle. */
export function Donut({
  parts,
  big,
  small,
  size = 150,
  width = 22,
  label,
}: {
  parts: Part[];
  big: ReactNode;
  small?: string;
  size?: number;
  width?: number;
  label: string;
}) {
  const r = (size - width) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const total = parts.reduce((sum, p) => sum + p.value, 0);
  const arcs = donutArcs(
    parts.map((p) => p.value),
    circ,
  );
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className="mx-auto block"
      role="img"
      aria-label={label}
    >
      <circle cx={c} cy={c} r={r} fill="none" stroke={C.track} strokeWidth={width} />
      {arcs.map((arc) => {
        const part = parts[arc.index];
        if (!part) {
          return null;
        }
        return (
          <circle
            key={part.id}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={part.color}
            strokeWidth={width}
            strokeDasharray={`${arc.length.toFixed(2)} ${(circ - arc.length).toFixed(2)}`}
            strokeDashoffset={(-arc.offset).toFixed(2)}
            transform={`rotate(-90 ${c} ${c})`}
            className={cn(part.onSelect && "cursor-pointer hover:opacity-80")}
            onClick={part.onSelect}
          >
            <title>{`${part.label}: ${part.value} (${pctOf(part.value, total)}%)`}</title>
          </circle>
        );
      })}
      <text x={c} y={c + 2} textAnchor="middle" className="fill-fg text-[32px] font-extrabold">
        {big}
      </text>
      {small ? (
        <text x={c} y={c + 22} textAnchor="middle" className="fill-fg-muted text-[12px]">
          {small}
        </text>
      ) : null}
    </svg>
  );
}

/** A ring for a percentage, with an optional goal tick. */
export function Ring({
  pct,
  color,
  big,
  small,
  goal,
  onSelect,
  label,
  size = 150,
  width = 16,
}: {
  pct: number | null | undefined;
  color: string;
  big: ReactNode;
  small?: string;
  goal?: number | null;
  onSelect?: () => void;
  label: string;
  size?: number;
  width?: number;
}) {
  const value = Math.max(0, Math.min(100, pct ?? 0));
  const tick = goal !== null && goal !== undefined ? goalTick(goal, size, width) : null;
  return (
    <div className="relative">
      <Donut
        parts={[
          { id: "value", label, value, color, onSelect },
          { id: "rest", label, value: 100 - value, color: C.track, onSelect },
        ]}
        big={big}
        small={small}
        size={size}
        width={width}
        label={label}
      />
      {tick ? (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="pointer-events-none absolute inset-0 mx-auto"
          aria-hidden
        >
          <line
            x1={tick[0]}
            y1={tick[1]}
            x2={tick[2]}
            y2={tick[3]}
            stroke="#212529"
            strokeWidth={3}
          />
        </svg>
      ) : null}
    </div>
  );
}

/** The key under a chart: one button per part (the keyboard path to each number). */
export function Key({ parts, total }: { parts: Part[]; total?: number }) {
  return (
    <div className="mt-1 flex flex-wrap justify-center gap-x-3.5 gap-y-1 text-[13px]">
      {parts.map((part) => (
        <button
          key={part.id}
          type="button"
          disabled={!part.onSelect}
          onClick={part.onSelect}
          className="hover:bg-bg-subtle focus-visible:ring-accent inline-flex items-center gap-1.5 rounded px-1 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default disabled:hover:bg-transparent"
        >
          <i
            className="inline-block h-[11px] w-[11px] rounded-[3px]"
            style={{ background: part.color }}
            aria-hidden
          />
          <span>{part.label}</span>
          <b>{part.value}</b>
          {part.extra !== undefined ? (
            <em className="text-fg-muted not-italic">{part.extra}</em>
          ) : total !== undefined ? (
            <em className="text-fg-muted not-italic">{pctOf(part.value, total)}%</em>
          ) : null}
        </button>
      ))}
    </div>
  );
}

const dayLabel = (day: string, locale: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(locale, { timeZone: "UTC", ...opts }).format(
    new Date(`${day}T12:00:00Z`),
  );

/** Raised vs resolved per day: two lines; a click on a day lists that day's tickets. */
export function FlowChart({
  days,
  locale,
  labels,
  onSelect,
  w = 440,
  h = 140,
}: {
  days: { date: string; created: number; resolved: number }[];
  locale: string;
  labels: { raised: string; resolved: string };
  onSelect: (day: string, which: "created" | "resolved") => void;
  w?: number;
  h?: number;
}) {
  if (days.length < 2) {
    return null;
  }
  const a = days.map((d) => d.created);
  const b = days.map((d) => d.resolved);
  const top = Math.max(1, ...a, ...b);
  const X = (i: number) => 30 + ((w - 40) * i) / (days.length - 1);
  const Y = (v: number) => 8 + (h - 30) * (1 - v / top);
  const line = (vs: number[]) =>
    vs.map((v, i) => `${i === 0 ? "M" : "L"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(" ");
  const step = (w - 40) / (days.length - 1);
  const short = (d: string) => dayLabel(d, locale, { weekday: "short", day: "2-digit" });
  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-auto w-full"
        role="img"
        aria-label={labels.raised}
      >
        <line x1={30} x2={w - 10} y1={Y(0)} y2={Y(0)} stroke="#d0d5dd" />
        <text x={26} y={Y(top) + 4} textAnchor="end" className="fill-fg-muted text-[10px]">
          {top}
        </text>
        <text x={26} y={Y(0) + 4} textAnchor="end" className="fill-fg-muted text-[10px]">
          0
        </text>
        <path d={line(a)} fill="none" stroke="#2a78d6" strokeWidth={2} />
        <path d={line(b)} fill="none" stroke="#eb6834" strokeWidth={2} />
        {days.map((d, i) => (
          <g key={d.date}>
            <rect
              x={X(i) - step / 2}
              y={0}
              width={step}
              height={(h - 20) / 2}
              fill="transparent"
              className="cursor-pointer hover:fill-[#2a78d61a]"
              onClick={() => onSelect(d.date, "created")}
            >
              <title>{`${short(d.date)}: ${labels.raised} ${d.created}, ${labels.resolved} ${d.resolved}`}</title>
            </rect>
            <rect
              x={X(i) - step / 2}
              y={(h - 20) / 2}
              width={step}
              height={(h - 20) / 2}
              fill="transparent"
              className="cursor-pointer hover:fill-[#eb68341a]"
              onClick={() => onSelect(d.date, "resolved")}
            >
              <title>{`${short(d.date)}: ${labels.resolved} ${d.resolved}`}</title>
            </rect>
          </g>
        ))}
        <text x={30} y={h - 4} className="fill-fg-muted text-[10px]">
          {short(days[0]?.date ?? "")}
        </text>
        <text x={w - 10} y={h - 4} textAnchor="end" className="fill-fg-muted text-[10px]">
          {short(days[days.length - 1]?.date ?? "")}
        </text>
      </svg>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[12.5px]">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-[#2a78d6]" aria-hidden />
          {labels.raised}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-[#eb6834]" aria-hidden />
          {labels.resolved}
        </span>
      </div>
    </div>
  );
}

export interface DayBar {
  date: string;
  total: number;
  share: number | null | undefined;
  parts: Part[];
}

/**
 * Per day, by channel: stacked bars, the day's total on top, the IT HelpDesk
 * share at the foot of each bar (days with 10+ tickets), weekends greyed.
 */
export function ChannelBars({
  days,
  locale,
  label,
  onSelectDay,
  w = 1040,
  h = 230,
}: {
  days: DayBar[];
  locale: string;
  label: string;
  onSelectDay: (day: string) => void;
  w?: number;
  h?: number;
}) {
  if (!days.length) {
    return null;
  }
  const top = Math.max(1, ...days.map((d) => d.total));
  const left = 34;
  const plotH = h - 40 - 26;
  const slot = (w - left - 10) / days.length;
  const bw = Math.min(46, slot * 0.62);
  const Y = (v: number) => 26 + plotH * (1 - v / top);
  const many = days.length > 20;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="block h-auto w-full" role="img" aria-label={label}>
      <line x1={left} x2={w - 10} y1={Y(0)} y2={Y(0)} stroke="#ced4da" />
      <text x={left - 4} y={Y(top) + 4} textAnchor="end" className="fill-fg-muted text-[10px]">
        {top}
      </text>
      <text x={left - 4} y={Y(0) + 4} textAnchor="end" className="fill-fg-muted text-[10px]">
        0
      </text>
      {days.map((d, i) => {
        const x = left + slot * i + (slot - bw) / 2;
        const weekend = isWeekend(d.date);
        const axis = cn("text-[10px]", weekend ? "fill-[#ced4da]" : "fill-fg-muted");
        let y0 = 0;
        return (
          <g key={d.date}>
            {d.parts.map((p) => {
              const rect = (
                <rect
                  key={p.id}
                  x={x}
                  y={Y(y0 + p.value)}
                  width={bw}
                  height={Math.max(0, Y(y0) - Y(y0 + p.value) - (p.value ? 1 : 0))}
                  fill={p.color}
                  className="cursor-pointer hover:opacity-80"
                  onClick={p.onSelect}
                  tabIndex={-1}
                  onKeyDown={(e) => activate(e, p.onSelect)}
                >
                  <title>{`${dayLabel(d.date, locale, { weekday: "short", day: "2-digit" })} · ${p.label}: ${p.value}`}</title>
                </rect>
              );
              y0 += p.value;
              return rect;
            })}
            <text
              x={x + bw / 2}
              y={Y(d.total) - 5}
              textAnchor="middle"
              className="fill-fg cursor-pointer text-[12px] font-bold"
              onClick={() => onSelectDay(d.date)}
            >
              {d.total}
            </text>
            {(!many || i % 2 === 0) && (
              <>
                <text x={x + bw / 2} y={h - 22} textAnchor="middle" className={axis}>
                  {dayLabel(d.date, locale, { weekday: "short" })}
                </text>
                <text x={x + bw / 2} y={h - 10} textAnchor="middle" className={axis}>
                  {dayLabel(d.date, locale, { day: "2-digit", month: "short" })}
                </text>
              </>
            )}
            {d.total >= 10 && d.share !== null && d.share !== undefined ? (
              <text
                x={x + bw / 2}
                y={Y(0) - 5}
                textAnchor="middle"
                className="pointer-events-none fill-white text-[11px] font-bold"
              >
                {`${Math.round(d.share)}%`}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export interface TeamBarRow {
  team: string;
  open: number;
  late: number;
  waiting: number;
  older: number;
  movesIn: number;
  movesOut: number;
}

/** Open tickets by team: bar = open now, red part = past SLA target, then ⏸ ⌛ ⇄. */
export function TeamBars({
  rows,
  labels,
  onSelect,
}: {
  rows: TeamBarRow[];
  labels: {
    pastTarget: (n: number) => string;
    waiting: string;
    older: string;
    moves: string;
  };
  onSelect: (team: string, column: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.open));
  const chip =
    "hover:bg-bg-subtle focus-visible:ring-accent rounded px-1 focus-visible:ring-2 focus-visible:outline-none";
  return (
    <div>
      {rows.map((r) => (
        <div
          key={r.team}
          className="border-border-subtle grid grid-cols-[minmax(8rem,230px)_1fr_auto] items-center gap-3 border-t py-1.5 print:grid-cols-[200px_1fr_190px]"
        >
          <button
            type="button"
            className={cn("truncate text-start text-[13.5px] font-semibold", chip)}
            onClick={() => onSelect(r.team, "open")}
            title={r.team}
          >
            {r.team}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={`${r.team}: ${r.open}`}
              className="h-[18px] min-w-1 cursor-pointer overflow-hidden rounded-[5px]"
              style={{ width: `${(100 * r.open) / max}%`, background: C.open }}
              onClick={() => onSelect(r.team, "open")}
            >
              <span
                className="block h-full"
                style={{ width: `${r.open ? (100 * r.late) / r.open : 0}%`, background: C.late }}
              />
            </button>
            <span className="text-[13px] whitespace-nowrap">
              <b>{r.open}</b>
              {r.late ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className={cn("font-bold text-[#e03131]", chip)}
                    onClick={() => onSelect(r.team, "late")}
                  >
                    {labels.pastTarget(r.late)}
                  </button>
                </>
              ) : null}
            </span>
          </div>
          <div className="text-fg-muted flex justify-end gap-2 text-[12.5px]">
            <button
              type="button"
              className={chip}
              title={labels.waiting}
              onClick={() => onSelect(r.team, "waiting")}
            >
              ⏸ {r.waiting}
            </button>
            <button
              type="button"
              className={chip}
              title={labels.older}
              onClick={() => onSelect(r.team, "older")}
            >
              ⌛ {r.older}
            </button>
            <span title={labels.moves} className="inline-flex items-center">
              ⇄{" "}
              <button type="button" className={chip} onClick={() => onSelect(r.team, "in")}>
                {r.movesIn}
              </button>
              /
              <button type="button" className={chip} onClick={() => onSelect(r.team, "out")}>
                {r.movesOut}
              </button>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
